import React from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import {
  countUniqueExercises,
  getBestAttemptsByExercise
} from '../utils/studentProgressInsights'
import './WeeklyStats.css'

function WeeklyStats({ attempts }) {
  if (!attempts || attempts.length === 0) {
    return null
  }

  const weeklyData = calculateWeeklyStats(attempts)

  if (weeklyData.total === 0) {
    return (
      <div className="weekly-stats-empty">
        <div className="weekly-stats-empty-emoji">📅</div>
        <p className="weekly-stats-empty-text">Aucune activité cette semaine</p>
      </div>
    )
  }

  const bestDay = weeklyData.days.reduce((best, day) =>
    day.count > best.count ? day : best
  , weeklyData.days[0])

  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
  const colors = ['#667eea', '#764ba2', '#8b5cf6', '#667eea', '#764ba2', '#8b5cf6', '#667eea']

  return (
    <div className="weekly-stats">
      <h3 className="weekly-stats-title">Cette Semaine</h3>

      <div className="weekly-stats-summary">
        <div className="weekly-stat-card">
          <div className="weekly-stat-value">{weeklyData.uniqueTotal}</div>
          <div className="weekly-stat-label">Exercices uniques</div>
        </div>
        <div className="weekly-stat-card">
          <div className="weekly-stat-value">{weeklyData.sessionTotal}</div>
          <div className="weekly-stat-label">Sessions</div>
        </div>
        <div className="weekly-stat-card">
          <div className="weekly-stat-value">{Math.round(weeklyData.avgScore)}%</div>
          <div className="weekly-stat-label">Score moyen (records)</div>
        </div>
        <div className="weekly-stat-card">
          <div className="weekly-stat-value">{dayNames[bestDay.index]}</div>
          <div className="weekly-stat-label">Meilleur jour</div>
        </div>
      </div>

      <div className="weekly-stats-chart">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={weeklyData.days} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fill: '#64748b', fontSize: 12, fontWeight: 500 }}
              tickLine={false}
              axisLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
            />
            <YAxis
              tick={{ fill: '#64748b', fontSize: 12, fontWeight: 500 }}
              tickLine={false}
              axisLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload
                  return (
                    <div className="weekly-stats-tooltip">
                      <p className="weekly-stats-tooltip-label">{data.name}</p>
                      <p className="weekly-stats-tooltip-value">
                        {data.uniqueCount} exercice{data.uniqueCount !== 1 ? 's' : ''} unique{data.uniqueCount !== 1 ? 's' : ''}
                        {data.sessionCount > data.uniqueCount ? ` · ${data.sessionCount} sessions` : ''}
                      </p>
                      {data.avgScore > 0 && (
                        <p className="weekly-stats-tooltip-score">Score moyen (records): {Math.round(data.avgScore)}%</p>
                      )}
                    </div>
                  )
                }
                return null
              }}
            />
            <Bar dataKey="uniqueCount" radius={[8, 8, 0, 0]}>
              {weeklyData.days.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

function calculateWeeklyStats(attempts) {
  const now = new Date()
  const dayOfWeek = now.getDay()
  const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)
  const weekStart = new Date(now.setDate(diff))
  weekStart.setHours(0, 0, 0, 0)

  const thisWeekAttempts = attempts.filter((a) => {
    if (!a.completedAt) return false
    const date = a.completedAt.toDate ? a.completedAt.toDate() : new Date(a.completedAt || 0)
    return date >= weekStart
  })

  const days = [
    { name: 'Lun', index: 0, count: 0, uniqueCount: 0, sessionCount: 0, totalScore: 0, attempts: [] },
    { name: 'Mar', index: 1, count: 0, uniqueCount: 0, sessionCount: 0, totalScore: 0, attempts: [] },
    { name: 'Mer', index: 2, count: 0, uniqueCount: 0, sessionCount: 0, totalScore: 0, attempts: [] },
    { name: 'Jeu', index: 3, count: 0, uniqueCount: 0, sessionCount: 0, totalScore: 0, attempts: [] },
    { name: 'Ven', index: 4, count: 0, uniqueCount: 0, sessionCount: 0, totalScore: 0, attempts: [] },
    { name: 'Sam', index: 5, count: 0, uniqueCount: 0, sessionCount: 0, totalScore: 0, attempts: [] },
    { name: 'Dim', index: 6, count: 0, uniqueCount: 0, sessionCount: 0, totalScore: 0, attempts: [] }
  ]

  thisWeekAttempts.forEach((attempt) => {
    const date = attempt.completedAt.toDate ? attempt.completedAt.toDate() : new Date(attempt.completedAt || 0)
    const dayIndex = (date.getDay() + 6) % 7

    if (dayIndex >= 0 && dayIndex < 7) {
      days[dayIndex].count++
      days[dayIndex].sessionCount++
      days[dayIndex].attempts.push(attempt)
    }
  })

  const daysWithStats = days.map((day) => {
    const bestDay = getBestAttemptsByExercise(day.attempts)
    const uniqueCount = countUniqueExercises(day.attempts)
    const avgScore =
      bestDay.length > 0
        ? bestDay.reduce((sum, a) => sum + (a.score || 0), 0) / bestDay.length
        : 0
    return {
      ...day,
      uniqueCount,
      count: uniqueCount,
      avgScore
    }
  })

  const weekBest = getBestAttemptsByExercise(thisWeekAttempts)
  const uniqueTotal = countUniqueExercises(thisWeekAttempts)
  const sessionTotal = thisWeekAttempts.length
  const avgScore =
    weekBest.length > 0
      ? weekBest.reduce((sum, a) => sum + (a.score || 0), 0) / weekBest.length
      : 0

  return {
    days: daysWithStats,
    total: uniqueTotal,
    uniqueTotal,
    sessionTotal,
    avgScore
  }
}

export default WeeklyStats
